/**
 * SA / 社員 用 価格表 (v3.2.12 / SA-4)。
 *
 * URL: /sa/catalogs
 *
 * 現在「適用中」(isActive=true) の商品カタログを一覧表示。
 * 各カタログをクリックで items 一覧 (展開) 表示。
 */
import { ChevronDown, ChevronRight, Tag } from 'lucide-react';
import { useMemo, useState } from 'react';

import { EmptyState } from '@/components/common/EmptyState';
import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import {
  useAllActiveCatalogItems,
  useProductCatalogs,
} from '@/hooks/useProductCatalogs';

export function SaCatalogsPage() {
  const catalogs = useProductCatalogs();
  const allItems = useAllActiveCatalogItems();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const active = useMemo(() => {
    return (catalogs.data ?? []).filter((c) => c.isActive);
  }, [catalogs.data]);

  const itemsByCatalog = useMemo(() => {
    const map = new Map<string, typeof allItems.data>();
    for (const it of allItems.data ?? []) {
      const list = map.get(it.catalogId) ?? [];
      list.push(it);
      map.set(it.catalogId, list);
    }
    return map;
  }, [allItems.data]);

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <Screen
      title="価格表"
      description="現在「適用中」の商品カタログ一覧。各カタログをクリックで商品の単価を確認できます。"
    >
      {catalogs.isLoading || allItems.isLoading ? (
        <Spinner />
      ) : active.length === 0 ? (
        <EmptyState
          icon={Tag}
          title="適用中のカタログがありません"
          description="管理者がカタログを「適用中」に設定するとここに表示されます。"
        />
      ) : (
        <div className="space-y-2">
          {active.map((c) => {
            const isOpen = expanded.has(c.id);
            const items = itemsByCatalog.get(c.id) ?? [];
            return (
              <div
                key={c.id}
                className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => toggle(c.id)}
                  className="w-full flex items-center gap-3 p-3 hover:bg-[var(--bg-hover)] transition-colors text-left"
                >
                  {isOpen ? (
                    <ChevronDown className="size-4 text-[var(--text-muted)]" />
                  ) : (
                    <ChevronRight className="size-4 text-[var(--text-muted)]" />
                  )}
                  <Tag className="size-4 text-[var(--text-muted)]" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">
                      {c.category}
                      <span className="ml-2 text-[11px] text-[var(--text-muted)]">
                        {c.label || c.version}
                      </span>
                    </div>
                  </div>
                  <Badge variant="approved">適用中</Badge>
                  <span className="text-[11px] text-[var(--text-muted)] tabular-nums">
                    {items.length} 件
                  </span>
                </button>
                {isOpen && (
                  <div className="border-t border-[var(--border)]">
                    <div className="grid grid-cols-[2fr_1fr_120px_120px] gap-2 px-3 py-2 bg-[var(--bg-elevated)] text-[10px] font-medium uppercase tracking-wider text-[var(--text-muted)]">
                      <div>商品</div>
                      <div>SKU</div>
                      <div className="text-right">税抜</div>
                      <div className="text-right">税込</div>
                    </div>
                    {items.map((it) => (
                      <div
                        key={it.id}
                        className="grid grid-cols-[2fr_1fr_120px_120px] gap-2 px-3 py-1.5 text-xs border-t border-[var(--border)]"
                      >
                        <div className="min-w-0 truncate">
                          {it.name}
                          {it.variation && (
                            <span className="text-[10px] text-[var(--text-muted)] ml-1">
                              ({it.variation})
                            </span>
                          )}
                        </div>
                        <div className="text-[var(--text-muted)] truncate font-mono text-[10px]">
                          {it.sku}
                        </div>
                        <div className="text-right tabular-nums">
                          ¥{it.priceExcludingTax.toLocaleString()}
                        </div>
                        <div className="text-right tabular-nums font-medium">
                          ¥{it.priceIncludingTax.toLocaleString()}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Screen>
  );
}
